import React from 'react';
import './App.css';
import TenantHeader from './TenantHeader';
import { formatListingPricePerMonth, useTenantPortalProfile } from './TenantPortalContext';
import { supabase } from './supabaseClient';

const rentStatusLabel = {
  paid: 'Paid',
  pending: 'Pending',
  overdue: 'Overdue',
  partial: 'Partial',
  waived: 'Waived',
};

function currentPeriodStartKey() {
  const now = new Date();
  const ys = now.getFullYear();
  const ms = String(now.getMonth() + 1).padStart(2, '0');
  return `${ys}-${ms}-01`;
}

function formatMonthLong(periodStart) {
  if (!periodStart) return '—';
  try {
    const d = new Date(`${periodStart}T12:00:00`);
    if (Number.isNaN(d.getTime())) return periodStart;
    return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  } catch {
    return periodStart;
  }
}

function formatMoney(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return `£${v.toLocaleString()}`;
}

function describePeriodStatus(p) {
  const due = Number(p.amount_due) || 0;
  const paid = Number(p.amount_paid) || 0;
  if (p.status === 'paid') return { label: 'Paid', badge: 'completed' };
  if (p.status === 'waived') return { label: 'Waived', badge: 'completed' };
  if (p.status === 'partial' || (paid > 0 && paid < due)) {
    return { label: 'Partial', badge: 'in-progress' };
  }
  const dueDate = p.due_date ? new Date(`${p.due_date}T12:00:00`) : null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (dueDate && dueDate < today && due > paid) {
    return { label: 'Overdue', badge: 'pending' };
  }
  return { label: 'Pending', badge: 'pending' };
}

function summaryFromCurrentMonth(periods, thisMonthKey, profileRentStatus) {
  const row = periods.find((p) => p.period_start === thisMonthKey);
  if (row) {
    const { label, badge } = describePeriodStatus(row);
    return { label, badge, source: 'month' };
  }
  const rs = profileRentStatus || 'pending';
  return {
    label: rentStatusLabel[rs] || rs || '—',
    badge:
      rs === 'paid'
        ? 'completed'
        : rs === 'overdue'
          ? 'pending'
          : 'pending',
    source: 'profile',
  };
}

function nextUpcomingLine(periods, rentDueDay) {
  const sorted = [...periods].sort((a, b) => a.period_start.localeCompare(b.period_start));
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const unpaid = sorted.filter((p) => {
    const due = Number(p.amount_due) || 0;
    const paid = Number(p.amount_paid) || 0;
    if (p.status === 'paid' || p.status === 'waived') return false;
    return paid < due;
  });
  const next = unpaid.find((p) => {
    const d = p.due_date ? new Date(`${p.due_date}T12:00:00`) : null;
    return d && !Number.isNaN(d.getTime()) && d >= today;
  });
  if (next?.due_date) {
    const d = new Date(`${next.due_date}T12:00:00`);
    const when = d.toLocaleDateString(undefined, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
    return `Next amount due: ${formatMoney(next.amount_due)} by ${when} (${formatMonthLong(next.period_start)}).`;
  }
  if (rentDueDay) {
    return `Rent is normally due on day ${rentDueDay} of each month. Your office will record each month here when it is set up.`;
  }
  return 'When your agent adds rent months for you, due dates and balances will show here.';
}

function buildChartModel(periods) {
  const sorted = [...periods].sort((a, b) => a.period_start.localeCompare(b.period_start)).slice(-6);
  if (sorted.length === 0) return null;
  const maxY = Math.max(
    1,
    ...sorted.map((p) => Math.max(Number(p.amount_due) || 0, Number(p.amount_paid) || 0))
  );
  const pad = 12;
  const w = 216;
  const h = 72;
  const n = sorted.length;
  const paidPts = sorted
    .map((p, i) => {
      const x = pad + (n === 1 ? w / 2 : (i / Math.max(1, n - 1)) * w);
      const paid = Number(p.amount_paid) || 0;
      const y = pad + h - (paid / maxY) * h;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  const duePts = sorted
    .map((p, i) => {
      const x = pad + (n === 1 ? w / 2 : (i / Math.max(1, n - 1)) * w);
      const due = Number(p.amount_due) || 0;
      const y = pad + h - (due / maxY) * h;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  const labels = sorted.map((p) =>
    new Date(`${p.period_start}T12:00:00`).toLocaleDateString(undefined, { month: 'short' })
  );
  return { paidPts, duePts, labels, maxY };
}

function TenantRent() {
  const { profile, property } = useTenantPortalProfile();
  const [periods, setPeriods] = React.useState([]);
  const [periodsLoading, setPeriodsLoading] = React.useState(true);
  const [periodsError, setPeriodsError] = React.useState('');
  const [selectedMonth, setSelectedMonth] = React.useState('all');

  const thisMonthKey = React.useMemo(() => currentPeriodStartKey(), []);

  const loadPeriods = React.useCallback(async () => {
    if (!supabase || !profile?.id) {
      setPeriods([]);
      setPeriodsLoading(false);
      return;
    }
    setPeriodsLoading(true);
    setPeriodsError('');
    const { data, error } = await supabase
      .from('tenant_rent_periods')
      .select('*')
      .eq('prospective_tenant_id', profile.id)
      .order('period_start', { ascending: false });

    if (error) {
      setPeriodsError(error.message);
      setPeriods([]);
    } else {
      setPeriods(data || []);
    }
    setPeriodsLoading(false);
  }, [profile?.id]);

  React.useEffect(() => {
    loadPeriods();
  }, [loadPeriods]);

  React.useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible') loadPeriods();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [loadPeriods]);

  React.useEffect(() => {
    setSelectedMonth('all');
  }, [profile?.id]);

  const monthOptions = React.useMemo(() => {
    const opts = [{ value: 'all', label: 'All months' }];
    const seen = new Set();
    periods.forEach((p) => {
      if (!p.period_start || seen.has(p.period_start)) return;
      seen.add(p.period_start);
      opts.push({
        value: p.period_start,
        label: formatMonthLong(p.period_start),
      });
    });
    return opts;
  }, [periods]);

  const filteredPeriods = React.useMemo(() => {
    if (selectedMonth === 'all') return periods;
    return periods.filter((p) => p.period_start === selectedMonth);
  }, [periods, selectedMonth]);

  const chartModel = React.useMemo(() => buildChartModel(periods), [periods]);

  const unitLine = React.useMemo(() => {
    const name = (property?.name || '').trim() || 'Your property';
    const room = (profile.room_label || '').trim();
    return room ? `${name} · ${room}` : name;
  }, [property?.name, profile.room_label]);

  const listingRent = formatListingPricePerMonth(property?.price_per_month);
  const summary = summaryFromCurrentMonth(periods, thisMonthKey, profile.rent_status);
  const rentLabel = summary.label;
  const upcomingText = nextUpcomingLine(periods, profile.rent_due_day_of_month);

  return (
    <div className="tenant-root">
      <TenantHeader />

      <main className="tenant-rent-shell">
        <header className="tenant-rent-header">
          <div>
            <h1 className="tenant-title">Rent & payments</h1>
            <p className="tenant-subtitle">
              <strong>{profile.full_name}</strong> · {unitLine}.
              {listingRent
                ? ` Listed monthly rent: ${listingRent}.`
                : ' Monthly rent is not set on the property yet.'}
            </p>
          </div>
        </header>

        <section className="tenant-rent-summary">
          <article className="tenant-rent-card">
            <div className="tenant-rent-card-header">
              <div>
                <h2 className="tenant-card-label">Your unit</h2>
                {listingRent ? (
                  <>
                    <p className="tenant-rent-amount">{listingRent}</p>
                    <p className="tenant-card-sub" style={{ marginTop: 4 }}>
                      {unitLine}
                    </p>
                  </>
                ) : (
                  <>
                    <p className="tenant-rent-amount">{unitLine}</p>
                    <p className="tenant-card-sub" style={{ marginTop: 4 }}>
                      Admin has not set monthly rent on this property.
                    </p>
                  </>
                )}
              </div>
              <span className={`tenant-status-badge tenant-status-badge--${summary.badge}`}>
                {rentLabel}
              </span>
            </div>
            <p className="tenant-card-sub">
              {summary.source === 'month'
                ? 'This month’s status comes from your recorded rent on file.'
                : 'This month has no rent row yet; showing your general tenant status until admin adds months.'}
            </p>
          </article>

          <article className="tenant-rent-card">
            <h2 className="tenant-card-label">Contact on file</h2>
            <p className="tenant-rent-status">{profile.email || '—'}</p>
            <p className="tenant-card-sub">{profile.phone?.trim() || 'No phone number stored.'}</p>
          </article>

          <article className="tenant-rent-card">
            <h2 className="tenant-card-label">Upcoming</h2>
            <ul className="tenant-rent-upcoming">
              <li>
                <span>Next rent</span>
                <span className="tenant-rent-upcoming-label">{upcomingText}</span>
              </li>
            </ul>
          </article>
        </section>

        <section className="tenant-rent-main">
          <section className="tenant-panel">
            <div className="tenant-panel-header">
              <div>
                <h3 className="tenant-panel-title">Payment history</h3>
                <p className="tenant-panel-subtitle">
                  {periodsLoading
                    ? 'Loading your rent periods…'
                    : periodsError
                      ? `Could not load history: ${periodsError}`
                      : periods.length === 0
                        ? 'Your agent has not added any rent months yet. After they do, each month will appear here.'
                        : `Showing ${filteredPeriods.length} month${filteredPeriods.length === 1 ? '' : 's'} for ${profile.full_name}.`}
                </p>
              </div>
              <div className="tenant-rent-month-filter">
                <label className="field-label" htmlFor="rent-month">
                  Month
                </label>
                <div className="input-shell">
                  <select
                    id="rent-month"
                    className="input-element"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    disabled={periods.length === 0 && !periodsLoading}
                  >
                    {monthOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="tenant-rent-history">
              <div className="tenant-rent-history-header">
                <span>Month</span>
                <span>Due / paid</span>
                <span>Status</span>
                <span>Due date</span>
              </div>
              <ul className="tenant-rent-history-list">
                {periodsLoading ? (
                  <li>
                    <span>—</span>
                    <span>—</span>
                    <span className="tenant-status-badge tenant-status-badge--pending">Loading…</span>
                    <span>—</span>
                  </li>
                ) : null}
                {!periodsLoading && periodsError ? (
                  <li>
                    <span>—</span>
                    <span>—</span>
                    <span className="tenant-status-badge tenant-status-badge--pending">Error</span>
                    <span title={periodsError}>See note above</span>
                  </li>
                ) : null}
                {!periodsLoading && !periodsError && filteredPeriods.length === 0 ? (
                  <li>
                    <span>—</span>
                    <span>—</span>
                    <span className="tenant-status-badge tenant-status-badge--pending">No rows</span>
                    <span>—</span>
                  </li>
                ) : null}
                {!periodsLoading &&
                  !periodsError &&
                  filteredPeriods.map((p) => {
                  const { label, badge } = describePeriodStatus(p);
                  return (
                    <li key={p.id}>
                      <span>{formatMonthLong(p.period_start)}</span>
                      <span>
                        {formatMoney(p.amount_paid)} / {formatMoney(p.amount_due)}
                      </span>
                      <span className={`tenant-status-badge tenant-status-badge--${badge}`}>{label}</span>
                      <span>{p.due_date || '—'}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </section>

          <section className="tenant-panel">
            <div className="tenant-panel-header">
              <div>
                <h3 className="tenant-panel-title">Monthly overview</h3>
                <p className="tenant-panel-subtitle">
                  {chartModel
                    ? 'Paid (blue) vs amount due (dashed) for your last months on file.'
                    : 'Chart appears once your agent has added at least one rent month.'}
                </p>
              </div>
            </div>

            <div className="tenant-rent-chart">
              <div className="tenant-rent-chart-bg" />
              {chartModel ? (
                <svg className="tenant-rent-chart-svg" viewBox="0 0 240 120" aria-hidden="true">
                  <polyline
                    fill="none"
                    stroke="#94a3b8"
                    strokeWidth="2"
                    strokeDasharray="4 3"
                    strokeLinecap="round"
                    points={chartModel.duePts}
                  />
                  <polyline
                    fill="none"
                    stroke="#2563eb"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    points={chartModel.paidPts}
                  />
                </svg>
              ) : (
                <p className="tenant-card-sub" style={{ position: 'relative', zIndex: 1, padding: '24px 16px' }}>
                  No chart data yet.
                </p>
              )}
              <div className="tenant-rent-chart-xlabels">
                {(chartModel?.labels || []).map((lab, i) => (
                  <span key={`${lab}-${i}`}>{lab}</span>
                ))}
              </div>
            </div>

            <ul className="tenant-rent-notifications">
              <li>
                <span className="tenant-rent-notify-title">Your portal</span>
                <span className="tenant-rent-notify-sub">
                  Figures match what your property manager enters in Rent management ({profile.email}).
                </span>
              </li>
            </ul>
          </section>
        </section>
      </main>
    </div>
  );
}

export default TenantRent;
